import React from "react";

/**
 * Anivaze v4 — EFFECTS ANIMATION kit (REAL-ANIMATION §3). Classic 2D effects as
 * pure f(t): dust puffs on footfalls/landings, speed lines behind fast motion,
 * an impact star on the payoff. Each effect lives in a fixed time window from
 * its `at` and renders nothing outside it — deterministic, seeded, $0. These
 * are the frames-between-the-frames that make motion feel PHYSICAL.
 */

const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);
const rnd = (i: number, s = 1) => { const x = Math.sin((i + 1) * 12.9898 * s + 78.233) * 43758.5453; return x - Math.floor(x); };

/** A puff of dust: 4 seeded particles that scatter up-and-out, expand, fade. */
export const DustPuff: React.FC<{ x: number; y: number; at: number; t: number; u: number; color?: string; seed?: number }> = ({
  x, y, at, t, u, color = "#b9b3a4", seed = 0,
}) => {
  const life = 0.55;
  const p = (t - at) / life;
  if (p <= 0 || p >= 1) return null;
  const fade = 1 - p;
  return (
    <g opacity={0.55 * fade}>
      {[0, 1, 2, 3].map((i) => {
        const a = (rnd(i, seed + 1) - 0.5) * 2.4 - Math.PI / 2; // mostly upward
        const dist = u * (0.8 + rnd(i, seed + 2) * 1.6) * p;
        const r = u * (0.35 + 0.5 * rnd(i, seed + 3)) * (0.6 + p);
        return <circle key={i} cx={x + Math.cos(a) * dist * 1.6} cy={y + Math.sin(a) * dist} r={r} fill={color} />;
      })}
    </g>
  );
};

/** Horizontal speed lines trailing motion (dir = travel direction, -1|1). */
export const SpeedLines: React.FC<{ x: number; y: number; t: number; u: number; dir: -1 | 1; active: boolean; color?: string }> = ({
  x, y, t, u, dir, active, color = "#ffffff",
}) => {
  if (!active) return null;
  return (
    <g opacity={0.4} stroke={color} strokeWidth={u * 0.22} strokeLinecap="round">
      {[0, 1, 2].map((i) => {
        const len = u * (2.2 + rnd(i, 7) * 1.8) * (0.7 + 0.3 * Math.sin(t * 30 + i * 2));
        const ly = y - u * (1.5 + i * 2.1);
        const lx = x - dir * u * (1.4 + rnd(i, 9));
        return <line key={i} x1={lx} y1={ly} x2={lx - dir * len} y2={ly} />;
      })}
    </g>
  );
};

/** A little impact star: spokes pop out and fade — the "!" of the scene. */
export const ImpactStar: React.FC<{ x: number; y: number; at: number; t: number; u: number; color?: string }> = ({
  x, y, at, t, u, color = "#e8b34a",
}) => {
  const life = 0.5;
  const p = (t - at) / life;
  if (p <= 0 || p >= 1) return null;
  const grow = 1 - Math.pow(1 - clamp01(p * 1.6), 3); // fast out
  const fade = 1 - p;
  const inner = u * 0.9 * grow;
  const outer = u * (1.6 + 1.2 * grow);
  return (
    <g opacity={0.9 * fade} stroke={color} strokeWidth={u * 0.3} strokeLinecap="round">
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const a = (i / 6) * Math.PI * 2 + 0.35;
        return <line key={i} x1={x + Math.cos(a) * inner} y1={y + Math.sin(a) * inner} x2={x + Math.cos(a) * outer} y2={y + Math.sin(a) * outer} />;
      })}
    </g>
  );
};
